/**
 * UTM Zone mapping by department (ubigeo first 2 digits).
 * Peru spans zones 17S, 18S, 19S.
 */
const DEPT_TO_ZONE: Record<string, number> = {
  '20': 17, '14': 17, '07': 17, '06': 17, // Tumbes, Lambayeque, Cajamarca, Piura
  '17': 19, '21': 19, // Madre de Dios, Puno
}

export const getUtmZone = (ubigeoCode: string | null | undefined): number => {
  if (!ubigeoCode || ubigeoCode.length < 2) return 18
  return DEPT_TO_ZONE[ubigeoCode.substring(0, 2)] ?? 18
}

/**
 * Zona UTM a partir de una longitud WGS84 (misma fórmula que el backend
 * MineTrace.Shared.Utils.UtmConverter.GetUtmZoneFromLongitude).
 */
export const getUtmZoneFromLongitude = (lng: number): number =>
  Math.floor((lng + 180) / 6) + 1

/**
 * Determina la zona UTM desde la geometría WGS84 ya guardada (fuente de verdad),
 * promediando la longitud de los vértices del polígono. Coincide con la regla
 * del backend (centroide). Si no hay geometría válida, cae al mapeo por ubigeo.
 *
 * Esto evita el bug en el que la zona se re-derivaba por ubigeo (cayendo a 18)
 * al reabrir, desalineando el selector, los vértices y los componentes.
 */
export const getUtmZoneFromGeoJson = (
  geoJson: string | null | undefined,
  ubigeoCode: string | null | undefined,
): number => {
  if (geoJson) {
    try {
      const geo = JSON.parse(geoJson)
      const coords: number[][] = geo?.type === 'Polygon' ? geo.coordinates?.[0] ?? [] : []
      const lngs = coords
        .map((c) => c?.[0])
        .filter((x): x is number => Number.isFinite(x))
      if (lngs.length > 0) {
        const meanLng = lngs.reduce((a, b) => a + b, 0) / lngs.length
        const z = getUtmZoneFromLongitude(meanLng)
        // Perú abarca 17S–19S; cualquier valor fuera de rango cae al ubigeo.
        if (z >= 17 && z <= 19) return z
      }
    } catch {
      /* geometría inválida → fallback por ubigeo */
    }
  }
  return getUtmZone(ubigeoCode)
}

/**
 * ¿El par lat/lng cae dentro del rango geográfico válido WGS84?
 *
 * `utmToLatLng` es una serie truncada: con coordenadas UTM malas (zona
 * equivocada, N/E intercambiados, ceros o un componente muy lejano) devuelve
 * valores FINITOS pero fuera de rango (p. ej. lng > 180). Si esos puntos se
 * pasan a Leaflet (fitBounds / Marker), el cálculo de tiles produce límites
 * Infinity y el TileLayer entra en un bucle de tiles que congela el hilo.
 * Filtrar por `Number.isFinite` no basta — hay que validar el rango.
 */
export const isValidLatLng = (lat: number, lng: number): boolean =>
  Number.isFinite(lat) && Number.isFinite(lng) &&
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180

/**
 * Convert UTM (Northing, Easting, Zone South) to WGS84 (lat, lng).
 * Simplified formula for Southern hemisphere.
 */
export const utmToLatLng = (northing: number, easting: number, zone: number): { lat: number; lng: number } => {
  const k0 = 0.9996
  const a = 6378137.0
  const e = 0.081819191
  const e2 = e * e
  const ep2 = e2 / (1 - e2)

  const x = easting - 500000.0
  const y = northing - 10000000.0 // Southern hemisphere

  const centralMeridian = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180)

  const M = y / k0
  const mu = M / (a * (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256))

  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2))
  const phi1 = mu +
    (3 * e1 / 2 - 27 * e1 * e1 * e1 / 32) * Math.sin(2 * mu) +
    (21 * e1 * e1 / 16 - 55 * e1 * e1 * e1 * e1 / 32) * Math.sin(4 * mu) +
    (151 * e1 * e1 * e1 / 96) * Math.sin(6 * mu)

  const N1 = a / Math.sqrt(1 - e2 * Math.sin(phi1) * Math.sin(phi1))
  const T1 = Math.tan(phi1) * Math.tan(phi1)
  const C1 = ep2 * Math.cos(phi1) * Math.cos(phi1)
  const R1 = a * (1 - e2) / Math.pow(1 - e2 * Math.sin(phi1) * Math.sin(phi1), 1.5)
  const D = x / (N1 * k0)

  const lat = phi1 - (N1 * Math.tan(phi1) / R1) * (
    D * D / 2 -
    (5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D * D * D * D / 24 +
    (61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) * D * D * D * D * D * D / 720
  )

  const lng = centralMeridian + (
    D -
    (1 + 2 * T1 + C1) * D * D * D / 6 +
    (5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * D * D * D * D * D / 120
  ) / Math.cos(phi1)

  return {
    lat: lat * (180 / Math.PI),
    lng: lng * (180 / Math.PI),
  }
}

/**
 * Convert WGS84 (lat, lng) to UTM (Northing, Easting) for Southern hemisphere.
 */
export const latLngToUtm = (lat: number, lng: number, zone: number): { northing: number; easting: number } => {
  const k0 = 0.9996
  const a = 6378137.0
  const e = 0.081819191
  const e2 = e * e
  const ep2 = e2 / (1 - e2)

  const latRad = lat * (Math.PI / 180)
  const lngRad = lng * (Math.PI / 180)
  const centralMeridian = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180)

  const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad))
  const T = Math.tan(latRad) * Math.tan(latRad)
  const C = ep2 * Math.cos(latRad) * Math.cos(latRad)
  const A = Math.cos(latRad) * (lngRad - centralMeridian)

  const M = a * (
    (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256) * latRad -
    (3 * e2 / 8 + 3 * e2 * e2 / 32 + 45 * e2 * e2 * e2 / 1024) * Math.sin(2 * latRad) +
    (15 * e2 * e2 / 256 + 45 * e2 * e2 * e2 / 1024) * Math.sin(4 * latRad) -
    (35 * e2 * e2 * e2 / 3072) * Math.sin(6 * latRad)
  )

  const easting = k0 * N * (
    A + (1 - T + C) * A * A * A / 6 +
    (5 - 18 * T + T * T + 72 * C - 58 * ep2) * A * A * A * A * A / 120
  ) + 500000.0

  const northing = k0 * (
    M + N * Math.tan(latRad) * (
      A * A / 2 +
      (5 - T + 9 * C + 4 * C * C) * A * A * A * A / 24 +
      (61 - 58 * T + T * T + 600 * C - 330 * ep2) * A * A * A * A * A * A / 720
    )
  ) + 10000000.0 // Southern hemisphere

  return { northing: Math.round(northing), easting: Math.round(easting) }
}
